import React, { useCallback, useState } from "react";
import { Dropdown, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaDownload, FaFileExcel, FaFilePdf } from "react-icons/fa";
import FileSaver from "file-saver";
import ExcelJS from "exceljs";
import dateFormat from "dateformat";
import { generateRelatoriosPdf } from "../utils/relatoriosPdfGenerator";
import {
  buildMeasureAveragesRow,
  buildMeasureExportSheet,
} from "../../../utils/measureFields";
import { track } from "../../../analytics";

const ExportReportDropdown = ({
  user,
  permissionSGU,
  allData = [],
  equipmentOptions = [],
  selectedUnitsCount = 0,
  startDate = "",
  endDate = "",
  ultimoClicado = null,
  lastData = 29,
  filterData = "all",
  customFilter = {},
  hours = {},
  curvaSelecionada = "",
  fileName = "Relatório Unidades",
  onError,
}) => {
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);

  const canWrite = permissionSGU?.report?.write;
  const hasData = Array.isArray(allData) && allData.length > 0;
  const isBusy = loadingPdf || loadingExcel;

  const handleExportPdf = useCallback(async () => {
    if (!hasData || isBusy || !canWrite) return;

    setLoadingPdf(true);
    try {
      await generateRelatoriosPdf({
        user,
        allData,
        equipmentOptions,
        selectedUnitsCount,
        startDate,
        endDate,
        ultimoClicado,
        lastData,
        filterData,
        customFilter,
        hours,
        curvaSelecionada,
      });
      track("report_downloaded", {
        reportType: "pdf",
        feature: "relatorios",
        rows: Array.isArray(allData) ? allData.length : 0,
      });
    } catch (error) {
      console.error("Erro ao gerar PDF de relatórios:", error);
      if (typeof onError === "function") {
        onError({
          title: "Erro ao gerar PDF",
          message:
            "Não foi possível gerar o relatório em PDF. Tente novamente.",
        });
      }
    } finally {
      setLoadingPdf(false);
    }
  }, [
    allData,
    canWrite,
    curvaSelecionada,
    customFilter,
    endDate,
    equipmentOptions,
    filterData,
    hasData,
    hours,
    isBusy,
    lastData,
    onError,
    selectedUnitsCount,
    startDate,
    ultimoClicado,
    user,
  ]);

  const handleExportExcel = useCallback(async () => {
    if (!hasData || isBusy || !canWrite) return;

    setLoadingExcel(true);
    try {
      const units =
        JSON.parse(localStorage.getItem("units")) ||
        [JSON.parse(localStorage.getItem("cooperative"))] ||
        [];

      const handleGetUnit = (unitId) => {
        const result = units.find((item) => item._id === unitId);
        return result?.name || "--";
      };

      const { headers, mappedRows } = buildMeasureExportSheet({
        dataList: allData,
        user,
        handleGetUnit,
        dateFormat,
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");
      worksheet.addRow(headers);

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0C392E" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
      headerRow.height = 30;

      headers.forEach((header, index) => {
        worksheet.getColumn(index + 1).width = header.length + 20;
      });

      mappedRows.forEach((rowData, rowIndex) => {
        const row = worksheet.addRow(Object.values(rowData));
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowIndex % 2 === 0 ? "FFF4F4F4" : "FFCAEB8C" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      worksheet.addRow([]);

      const averagesRow = worksheet.addRow(
        buildMeasureAveragesRow(headers, allData)
      );

      averagesRow.eachCell((cell) => {
        cell.font = { bold: true, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8F5E9" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "medium" },
          left: { style: "thin" },
          bottom: { style: "medium" },
          right: { style: "thin" },
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      FileSaver.saveAs(
        blob,
        `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      track("report_downloaded", {
        reportType: "excel",
        feature: "relatorios",
        rows: Array.isArray(allData) ? allData.length : 0,
      });
    } catch (error) {
      console.error("Erro ao gerar Excel de relatórios:", error);
      if (typeof onError === "function") {
        onError({
          title: "Erro ao gerar Excel",
          message:
            "Não foi possível gerar o relatório em Excel. Tente novamente.",
        });
      }
    } finally {
      setLoadingExcel(false);
    }
  }, [allData, canWrite, fileName, hasData, isBusy, onError, user]);

  const toggle = (
    <Dropdown.Toggle
      variant="success"
      size="sm"
      className={`dash-btn ${!hasData ? "dash-btn--muted" : ""}`}
      disabled={!canWrite || isBusy}
      id="relatorios-download-toggle"
    >
      {isBusy ? (
        <>
          <Spinner animation="border" size="sm" />{" "}
          <span>{loadingPdf ? "Gerando PDF..." : "Gerando Excel..."}</span>
        </>
      ) : (
        <>
          <FaDownload />
          <span> Download</span>
        </>
      )}
    </Dropdown.Toggle>
  );

  return (
    <Dropdown align="end">
      {!hasData ? (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip>
              Carregue medições com os filtros para baixar o relatório.
            </Tooltip>
          }
        >
          <span className="d-inline-flex">{toggle}</span>
        </OverlayTrigger>
      ) : (
        toggle
      )}

      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
        <Dropdown.Item
          onClick={handleExportExcel}
          disabled={!canWrite || !hasData || isBusy}
        >
          <FaFileExcel className="me-2 text-success" />
          Excel (.xlsx)
        </Dropdown.Item>
        <Dropdown.Item
          onClick={handleExportPdf}
          disabled={!canWrite || !hasData || isBusy}
        >
          <FaFilePdf className="me-2 text-danger" />
          PDF
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ExportReportDropdown;
